import type { LetterGrade, ScanFinding, ScanGrade, ScanResult, Severity } from './types';

/** `GET /api/scan/report/:id` as vsuite returns it. Only the fields the banner reads. */
export interface RawReport {
  url?: string;
  summary?: Partial<Record<'critical' | 'high' | 'medium' | 'low' | 'info' | 'total', number>>;
  findings?: RawFinding[];
  ssl?: { grade?: string };
  detailedSSL?: {
    grade?: string;
    certificates?: Array<{ daysRemaining?: number; issuer?: string }>;
    protocols?: Array<{ version: string; supported: boolean; isSecure: boolean }>;
    vulnerabilities?: Array<{
      name: string;
      vulnerable: boolean;
      severity?: string;
      description?: string;
      mitigation?: string;
      cve?: string;
    }>;
    host?: string;
    port?: number | string;
  };
}

interface RawFinding {
  title?: string;
  description?: string;
  severity?: string;
  type?: string;
  parameter?: string;
  remediation?: string;
  url?: string;
  evidence?: string;
}

const SEVERITY: Record<string, Severity> = {
  CRITICAL: 'high',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info',
};

const RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2, info: 3 };

// Points taken off 100 per finding. A01 "sensitive path answered 200" is the noisiest
// check, so it is capped instead of letting one probe list sink the score on its own.
const PENALTY: Record<Severity, number> = { high: 12, medium: 3, low: 1, info: 0 };
const OWASP_PENALTY_CAP = 15;

// The seven headers the scanner's "N out of 7" counts. It reports only the missing ones by
// name, so the rest are shown as present.
const HEADER_NAMES = [
  'Content-Security-Policy',
  'Strict-Transport-Security',
  'X-Frame-Options',
  'X-Content-Type-Options',
  'Referrer-Policy',
  'Permissions-Policy',
  'X-XSS-Protection',
] as const;
const HEADERS_TOTAL = HEADER_NAMES.length;
const OWASP_RE = /^A(\d{2}):\d{4}$/;
// Findings the scanner does not tag with an A-code, filed under the group the design uses:
// header checks -> A05 Security Misconfiguration, obfuscated JS -> A08 Integrity Failures.
const OWASP_GROUP_BY_TYPE: Record<string, string> = {
  security_headers: '05',
  weak_security_header: '05',
  javascript_obfuscation: '08',
};
const EXPIRING_DAYS = 30;

const severityOf = (raw?: string): Severity => SEVERITY[(raw ?? '').toUpperCase()] ?? 'info';

function countBySeverity(findings: ReadonlyArray<Pick<ScanFinding, 'severity'>>): Record<Severity, number> {
  const counts: Record<Severity, number> = { high: 0, medium: 0, low: 0, info: 0 };
  for (const f of findings) counts[f.severity] += 1;
  return counts;
}

function letter(raw?: string): LetterGrade {
  const g = (raw ?? '').trim().toUpperCase().charAt(0);
  return g === 'A' ? 'A' : g === 'B' ? 'B' : g === 'C' ? 'C' : 'D';
}

function scoreOf(findings: ScanFinding[]): number {
  let owasp = 0;
  let rest = 0;
  for (const f of findings) {
    const p = PENALTY[f.severity];
    if (f.category && OWASP_RE.test(f.category)) owasp += p;
    else rest += p;
  }
  return Math.max(0, Math.min(100, 100 - rest - Math.min(owasp, OWASP_PENALTY_CAP)));
}

const gradeOf = (score: number): ScanGrade => (score >= 85 ? 'good' : score >= 65 ? 'attention' : 'risky');

export function mapReport(raw: RawReport): ScanResult {
  const rawFindings = raw.findings ?? [];
  const findings: ScanFinding[] = rawFindings
    .map((f) => ({
      severity: severityOf(f.severity),
      title: f.title ?? '',
      detail: f.description ?? '',
      category: f.type,
      fix: f.remediation,
      location: f.url ?? raw.url,
      parameter: f.parameter,
      evidence: f.evidence,
    }))
    .sort((a, b) => RANK[a.severity] - RANK[b.severity]);

  // Headers: the scanner folds its own tally into one INFO finding
  // ("Security Headers Score: 71% (Grade: C)" / "5 out of 7 recommended ...").
  const summary = rawFindings.find((f) => f.type === 'security_summary');
  const present = Number(/(\d+)\s+out of\s+(\d+)/.exec(summary?.description ?? '')?.[1]);
  const headerGrade = /Grade:\s*([A-F])/i.exec(summary?.title ?? '')?.[1];
  const missing = rawFindings
    .filter((f) => f.type === 'security_headers' && f.parameter)
    .map((f) => f.parameter as string);
  const headersPresent = Number.isFinite(present) ? present : Math.max(0, HEADERS_TOTAL - missing.length);

  const tls = raw.detailedSSL;
  const cert = tls?.certificates?.[0];
  const daysLeft = cert?.daysRemaining ?? 0;
  const secure = (tls?.protocols ?? []).filter((p) => p.supported && p.isSecure).map((p) => p.version);
  const version = secure.includes('TLS 1.3') ? 'TLS 1.3' : (secure.at(-1) ?? 'Unknown');

  const perCategory = Array.from({ length: 10 }, () => 0);
  for (const f of rawFindings) {
    const group = OWASP_RE.exec(f.type ?? '')?.[1] ?? OWASP_GROUP_BY_TYPE[f.type ?? ''];
    if (group) perCategory[Number(group) - 1] += 1;
  }

  const score = scoreOf(findings);
  return {
    score,
    grade: gradeOf(score),
    findings,
    counts: countBySeverity(findings),
    totalChecks: raw.summary?.total ?? findings.length,
    partial: tls?.certificates?.length ? '' : 'Could not read the website’s SSL/TLS certificate.',
    metrics: {
      owasp: {
        flagged: perCategory.filter((n) => n > 0).length,
        issues: perCategory.reduce((sum, n) => sum + n, 0),
        total: 10,
        perCategory,
      },
      headers: {
        grade: letter(headerGrade),
        score: Number(/(\d+)%/.exec(summary?.title ?? '')?.[1] ?? Math.round((headersPresent / HEADERS_TOTAL) * 100)),
        present: headersPresent,
        total: HEADERS_TOTAL,
        cells: HEADER_NAMES.map((name) => ({ name, present: !missing.includes(name) })),
        missing,
      },
      tls: {
        grade: letter(tls?.grade ?? raw.ssl?.grade),
        version,
        issuer: /CN=([^,]+)/.exec(cert?.issuer ?? '')?.[1] ?? cert?.issuer ?? '',
        daysLeft,
        expiring: daysLeft < EXPIRING_DAYS,
        protocols: (tls?.protocols ?? []).map((p) => ({ name: p.version, supported: p.supported, secure: p.isSecure })),
        vulns: (tls?.vulnerabilities ?? []).filter((v) => v.vulnerable).map((v) => v.name),
      },
    },
  };
}
